import { API_PREFIX } from '@glintfans/shared';

/** Base URL of the API. In production the Next.js rewrite proxies /api -> API. */
const API_BASE = process.env.NEXT_PUBLIC_API_BASE || '';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export interface AuthStorage {
  accessToken: string | null;
  refreshToken: string | null;
}

const AUTH_STORAGE_KEY = 'AiLumix-auth';

function readAuthStorage(): AuthStorage {
  if (typeof window === 'undefined') return { accessToken: null, refreshToken: null };
  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return { accessToken: null, refreshToken: null };
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    // Current format: { accessToken, refreshToken }
    if (typeof parsed.accessToken === 'string') {
      return {
        accessToken: parsed.accessToken,
        refreshToken: typeof parsed.refreshToken === 'string' ? parsed.refreshToken : null,
      };
    }
    // Legacy formats some browsers may still carry: { token } / { access_token, refresh_token }
    const legacy = parsed as Record<string, unknown>;
    const tk =
      typeof legacy.token === 'string'
        ? legacy.token
        : typeof legacy.access_token === 'string'
          ? legacy.access_token
          : null;
    const rt =
      typeof legacy.refresh_token === 'string'
        ? legacy.refresh_token
        : typeof legacy.refreshToken === 'string'
          ? legacy.refreshToken
          : null;
    if (tk || rt) return { accessToken: tk, refreshToken: rt };
    return { accessToken: null, refreshToken: null };
  } catch {
    return { accessToken: null, refreshToken: null };
  }
}

let authStorage: AuthStorage = readAuthStorage();

// D17-fix: concurrent 401s share ONE refresh round. The refresh endpoint rotates
// the token, so parallel retries with the same old refresh token would race:
// the first succeeds, the rest get 401 and log the user out despite a live session.
let refreshInFlight: Promise<boolean> | null = null;

function tryRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = doRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function doRefresh(): Promise<boolean> {
  const refresh = getRefreshToken();
  if (!refresh) return false;
  try {
    const res = await fetch(`${API_BASE}${API_PREFIX}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: refresh }),
    });
    if (!res.ok) {
      setAuthTokens(null);
      dispatchUnauthorized();
      return false;
    }
    const tokens = (await res.json()) as { accessToken: string; refreshToken: string };
    setAuthTokens(tokens);
    return true;
  } catch {
    // transient network failure — keep the session, the next request retries
    return false;
  }
}

export function setAuthTokens(tokens: { accessToken: string; refreshToken: string } | null): void {
  authStorage = tokens
    ? { accessToken: tokens.accessToken, refreshToken: tokens.refreshToken }
    : { accessToken: null, refreshToken: null };
  if (typeof window !== 'undefined') {
    try {
      if (tokens) {
        window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authStorage));
      } else {
        window.localStorage.removeItem(AUTH_STORAGE_KEY);
      }
    } catch {
      /* storage unavailable */
    }
  }
}

/** Fired when a stored session is no longer valid (e.g. refresh token expired). */
export const AUTH_UNAUTHORIZED_EVENT = 'AiLumix:unauthorized';

function dispatchUnauthorized(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT));
}

export function getAccessToken(): string | null {
  return authStorage.accessToken;
}

export function getRefreshToken(): string | null {
  return authStorage.refreshToken;
}

async function request<T>(path: string, options: RequestInit = {}, retried = false): Promise<T> {
  const isForm = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isForm ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers as Record<string, string> | undefined),
  };
  const token = getAccessToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${API_PREFIX}${path}`, { ...options, headers });

  if (res.status === 401 && !retried && getRefreshToken()) {
    const ok = await tryRefresh();
    if (ok) return request<T>(path, options, true);
  }

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      if (Array.isArray(body.message)) message = body.message.join(', ');
      else if (body.message) message = body.message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body !== undefined ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body !== undefined ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body !== undefined ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, formData: FormData) => request<T>(path, { method: 'POST', body: formData }),
};
